/**
 * Hooks de PRAZOS DE PAGAMENTO (payment terms) — espelham
 * `PaymentTermResponseDto` de treasury.dto.ts.
 *
 * Contratos consumidos (served-routes.json):
 *   GET    /finance/payment-terms?isActive&q&page&pageSize → PaginatedResponseDto<PaymentTermResponseDto>
 *   POST   /finance/payment-terms                          → PaymentTermResponseDto (PaymentTermInputDto)
 *   PUT    /finance/payment-terms/:id                      → PaymentTermResponseDto (PaymentTermUpdateInputDto)
 *   DELETE /finance/payment-terms/:id                      → { id, deleted }
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import type { PaginatedResponse } from './treasury/treasuryTypes';

/** treasury.dto.ts: PaymentTermResponseDto */
export interface PaymentTerm {
  id: string;
  code: string;
  name: string;
  discountPercent?: number | null;
  interestRate?: number | null;
  lateFeePercent?: number | null;
  maxInstallments: number;
  installmentInterval: string;
  installmentIntervalDays: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** treasury.dto.ts: PaymentTermInputDto (corpo do POST /finance/payment-terms) */
export interface PaymentTermInput {
  code: string;
  name: string;
  discountPercent?: number;
  interestRate?: number;
  lateFeePercent?: number;
  maxInstallments?: number;
  installmentInterval?: string;
  installmentIntervalDays?: number;
}

/** treasury.dto.ts: PaymentTermUpdateInputDto (corpo do PUT /finance/payment-terms/:id) */
export type PaymentTermUpdateInput = Partial<
  Pick<PaymentTermInput, 'name' | 'discountPercent' | 'interestRate' | 'lateFeePercent' | 'maxInstallments' | 'installmentInterval' | 'installmentIntervalDays'>
> & {
  isActive?: boolean;
};

/** treasury.dto.ts: ListParamsDto (query de GET /finance/payment-terms) */
export interface PaymentTermFilters {
  isActive?: boolean;
  q?: string;
  page?: number;
  pageSize?: number;
}

const paymentTermsKeys = {
  list: (filters: PaymentTermFilters) => ['finance', 'treasury', 'payment-terms', filters] as const,
};

export function usePaymentTerms(filters: PaymentTermFilters = {}) {
  return useQuery({
    queryKey: paymentTermsKeys.list(filters),
    queryFn: () =>
      apiClient.get<PaginatedResponse<PaymentTerm>>('/finance/payment-terms', {
        params: {
          page: String(filters.page ?? 1),
          pageSize: String(filters.pageSize ?? 50),
          ...(filters.isActive !== undefined ? { isActive: String(filters.isActive) } : {}),
          ...(filters.q ? { q: filters.q } : {}),
        },
      }),
  });
}

export function useCreatePaymentTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PaymentTermInput) =>
      apiClient.post<PaymentTerm>('/finance/payment-terms', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payment-terms'] });
    },
  });
}

export function useUpdatePaymentTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & PaymentTermUpdateInput) =>
      apiClient.put<PaymentTerm>(`/finance/payment-terms/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payment-terms'] });
    },
  });
}

export function useDeletePaymentTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ id: string; deleted: boolean }>(`/finance/payment-terms/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'treasury', 'payment-terms'] });
    },
  });
}

/* ── Rótulos de apresentação (derivados) ── */

export const INSTALLMENT_INTERVAL_LABEL: Record<string, string> = {
  DAILY: 'Diário',
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensal',
  CUSTOM_DAYS: 'Dias customizados',
};